import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Grid3x3, FileDown, Loader2, Link2Off, Moon, Sun } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { sortByOrder } from "../lib/matrix";
import { localizePlan, localizeAddon, regionMeta } from "../lib/pricing";
import { computePlan } from "../lib/planner";
import { downloadDocumentPdf } from "../lib/share";
import { ProposalBody } from "../components/workspace/ProposalCard";
import useTheme from "../hooks/useTheme";

export default function PublicProposalPage() {
  const { token } = useParams();
  const { theme, toggle } = useTheme();
  const [data, setData] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    document.title = "Shared proposal · Microsoft License Hub";
    Promise.all([api.publicProposal(token), api.matrix()])
      .then(([d, m]) => {
        setData(d);
        setDataset(m);
      })
      .catch((err) => setError(err?.response?.status === 404 ? "This proposal link is invalid or has been revoked." : errorMessage(err)));
  }, [token]);

  const region = data?.scenario.region || "us";
  const priceMode = data?.scenario.price_mode || "teams";
  const plans = useMemo(() => (dataset ? sortByOrder(dataset.plans).map((p) => localizePlan(p, region)) : []), [dataset, region]);
  const addons = useMemo(() => (dataset ? dataset.addons.map((a) => localizeAddon(a, region)) : []), [dataset, region]);
  const pricing = useMemo(() => regionMeta(dataset, region), [dataset, region]);
  const plan = useMemo(() => (dataset && data ? computePlan({ summary: data.scan.summary, moves: data.scenario.moves, plans, addons, rows: dataset.rows, priceMode }) : null), [dataset, data, plans, addons, priceMode]);

  const download = async () => {
    setExporting(true);
    try {
      await downloadDocumentPdf(ref.current, `${data.tenant.name}-proposal`);
    } catch (err) {
      toast.error("Could not create the PDF", { description: err?.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="public-page" data-testid="public-proposal-page">
      <header className="public-bar glass">
        <Link to="/" className="brand" data-testid="brand-link">
          <span className="brand-mark">
            <Grid3x3 size={18} strokeWidth={2.4} aria-hidden="true" />
          </span>
          <span className="brand-text">
            Microsoft <b>License Hub</b>
          </span>
        </Link>
        <span className="muted small">Read-only proposal shared by {data?.org?.name || "an organization"}</span>
        <span className="tenant-actions">
          <button type="button" className="icon-btn" onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {plan && (
            <button type="button" className="tb-btn sm" onClick={download} disabled={exporting} data-testid="public-download-pdf">
              {exporting ? <Loader2 size={13} className="spin" /> : <FileDown size={13} />} Download PDF
            </button>
          )}
        </span>
      </header>
      {error && (
        <div className="ws-empty glass public-error" data-testid="public-proposal-error">
          <Link2Off size={26} />
          <h3>Proposal unavailable</h3>
          <p>{error}</p>
          <Link to="/" className="tb-btn sm">
            Open the license matrix
          </Link>
        </div>
      )}
      {!error && !plan && (
        <div className="ws-empty">
          <Loader2 size={22} className="spin" /> Loading proposal...
        </div>
      )}
      {plan && (
        <div className="share-card proposal inline" ref={ref} data-testid="public-proposal">
          <ProposalBody tenant={data.tenant} scan={data.scan} plan={plan} plans={plans} rows={dataset.rows} priceMode={priceMode} pricing={pricing} scenarioName={data.scenario.name} shared />
        </div>
      )}
    </div>
  );
}
